// src/services/api.js — HorizonteCassaPyME
// Bridges Cassa POS to the HorizontePyME backend (same shared backend).
// Auth token injection is handled automatically by the axios interceptors
// registered in AuthProvider — no manual token headers needed here.

import axios from 'axios';
import { NODE_BACKEND_API_URL, NODE_BACKEND_PRD_URL, NODE_BACKEND_BRW_URL } from '../config';

// ---------------------------------------------------------------------------
// Payment method mapping: Cassa labels -> PyME numeric codes
// 201 Contado | 204 T.Credito | 205 T.Debito | 206 Transferencia
// 207 Mercado Pago | 208 Otra
// ---------------------------------------------------------------------------
const PAYMENT_METHOD_CODES = {
    efectivo:      '201',
    debito:        '205',
    credito:       '204',
    qr:            '207',
    transferencia: '206',
    rappi:         '207',
    pedidosya:     '208',
};

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function mapProduct(p) {
    return {
        id:          p.ProductId,
        name:        p.ProductDescription,
        price:       p.RetailPrice || 0,
        category:    p.CategoryName,
        brand:       p.BrandName,
        productCode: p.ProductCode,
        ean:         p.EAN,
        stock:       p.StockQuantity,
        _raw: p,
    };
}

function buildInvoiceItem(item) {
    const raw = item._raw || {};
    const unitPrice = item.price || 0;
    const ivaRate = 21;
    const divisor = 1 + ivaRate / 100;
    const basePrice = Number((unitPrice / divisor).toFixed(2));
    const ivaAmount = Number((unitPrice - basePrice).toFixed(2));

    return {
        ProductId:          raw.ProductId    || item.id          || null,
        ProductDescription: item.name,
        ProductCode:        raw.ProductCode  || item.productCode || '',
        EAN:                raw.EAN          || item.ean         || '',
        BrandName:          raw.BrandName    || item.brand       || '',
        CategoryName:       raw.CategoryName || item.category    || '',
        quantity:           item.quantity,
        splitter:           1,
        StockQuantity:      raw.StockQuantity || item.stock || null,
        RetailPrice:        unitPrice,
        BasePrice:          basePrice,
        IVAAmount:          ivaAmount,
        IVARate:            ivaRate,
        precio_con_iva:     unitPrice,
        precio_sin_iva:     basePrice,
        alicuota:           ivaRate,
        afecta_stock:       !!(raw.ProductId || item.id),
    };
}

// ---------------------------------------------------------------------------
// ApiService
// ---------------------------------------------------------------------------

class ApiService {

    // GET /apitest/getcategorynames
    // Backend may return string[] OR [{CategoryName:"..."},...] — normalise to string[]
    async getCategories() {
        const response = await axios.get(`${NODE_BACKEND_API_URL}/getcategorynames`);
        const data = response.data || [];
        return data.map(item =>
            typeof item === 'string' ? item : (item.CategoryName || item.categoryName || String(item))
        );
    }

    // GET /api/v3/browse?categoryName=<cat>&includeStockedOnly=false
    // /browse does not require a search term — returns all products in the category
    async getProductsByCategory(category) {
        const response = await axios.get(NODE_BACKEND_BRW_URL, {
            params: { categoryName: category, includeStockedOnly: false },
        });
        return (response.data || []).map(mapProduct);
    }

    // GET /api/v3/search — requires a non-empty search term
    async searchProducts(searchTerm, additionalFilters = {}) {
        const term = (searchTerm || '').trim();
        if (!term) return [];
        const params = { search: term, includeStockedOnly: false, ...additionalFilters };
        const response = await axios.get(NODE_BACKEND_PRD_URL + 'search', { params });
        return (response.data || []).map(mapProduct);
    }

    async getProducts(filters = {}) {
        if (filters.search && filters.search.trim()) {
            return this.searchProducts(filters.search, filters);
        }
        return this.getProductsByCategory(filters.categoryName || '');
    }

    // POST /apitest/invoices/create — Factura X (sale, no AFIP registration)
    async createOrder(orderData) {
        const paymentCode = PAYMENT_METHOD_CODES[orderData.paymentMethod] || '201';
        const subtotal    = orderData.items.reduce((s, i) => s + i.price * i.quantity, 0);
        const discount    = orderData.discount || 0;
        const discountAmt = Number((subtotal * (discount / 100)).toFixed(2));
        const total       = Number((subtotal - discountAmt).toFixed(2));

        const payload = {
            cartItems:          orderData.items.map(buildInvoiceItem),
            subtotal,
            total,
            discountPercentage: discount,
            discountAmount:     discountAmt,
            sessionId:          `cassa_${Date.now()}`,
            externalReference:  `CASSA-${Date.now()}`,
            invoiceType:        'X',
            paymentMethod:      paymentCode,
            clientData:         null,
            registerWithAFIP:   false,
            paymentMode:        'single',
            splitPayment:       null,
            notes:              orderData.notes || null,
        };

        const response = await axios.post(
            `${NODE_BACKEND_API_URL}/invoices/create`,
            payload,
            { timeout: 60000 },
        );
        return response.data;
    }

    // GET /apitest/invoices
    async getOrders(filters = {}) {
        const response = await axios.get(`${NODE_BACKEND_API_URL}/invoices`, { params: filters });
        return response.data;
    }

    async getOrder(invoiceId) {
        const response = await axios.get(`${NODE_BACKEND_API_URL}/invoices/${invoiceId}`);
        return response.data;
    }

    // GET /apitest/analytics/dashboard
    async getDashboardStats() {
        const response = await axios.get(`${NODE_BACKEND_API_URL}/analytics/dashboard`);
        return response.data;
    }

    async getSalesByCategory(filters = {}) {
        const response = await axios.get(
            `${NODE_BACKEND_API_URL}/analytics/sales-by-category`,
            { params: filters },
        );
        const data = response.data;
        // Backend may return an array or an object wrapping the array
        if (Array.isArray(data)) return data;
        if (data && Array.isArray(data.data)) return data.data;
        if (data && Array.isArray(data.categories)) return data.categories;
        return [];
    }

    async getHourlySales() {
        const response = await axios.get(`${NODE_BACKEND_API_URL}/analytics/daily-sales-stats`);
        return response.data;
    }

    async getAvailableDates() {
        const response = await axios.get(`${NODE_BACKEND_API_URL}/analytics/invoice-dates`);
        return response.data;
    }
}

const apiService = new ApiService();
export default apiService;
